// Pestaña Líneas: lista de líneas por grupos (urbanas, cementerios, nocturna, pedanías) con su
// interruptor de «mostrar en el mapa»; cada grupo tiene su botón para mostrarlas u ocultarlas
// todas. Tocar el nombre abre la ficha de la línea (lineView). El estado (qué líneas se ven) se guarda en el
// dispositivo y se puede sustituir temporalmente (ficha de línea, resultado de una ruta).

import type { Dataset, LineGroup, LineId } from "../data";
import type { MapAdapter } from "../map/types";
import { persistGet, persistSet } from "../persist";
import { badge, escapeHtml } from "./common";

const GROUP_TITLE: Record<LineGroup, string> = {
  urbana: "Urbanas",
  cementerio: "Cementerios",
  nocturna: "Nocturna",
  pedania: "Pedanías",
};

const STATE_KEY = "buselx";

export interface LinesState {
  on: LineId[];
}

const DEFAULT_STATE: LinesState = { on: ["M", "G", "I"] };

export function loadLinesState(): LinesState {
  const raw = persistGet<Partial<LinesState>>(STATE_KEY, DEFAULT_STATE);
  return { on: Array.isArray(raw.on) ? raw.on : DEFAULT_STATE.on };
}

export interface LinesPanelOptions {
  ds: Dataset;
  adapter: MapAdapter;
  el: HTMLElement;
  initial: LinesState;
  onChange?: (on: readonly LineId[]) => void;
  onOpenLine: (line: LineId) => void;
}

export interface LinesPanelHandle {
  readonly on: readonly LineId[];
  setOn(on: readonly LineId[]): void;
  // Muestra otras líneas sin tocar la selección guardada; `null` vuelve a la selección.
  setTemporary(on: readonly LineId[] | null): void;
}

export function mountLinesPanel(o: LinesPanelOptions): LinesPanelHandle {
  let on: LineId[] = o.initial.on.filter((l) => o.ds.linesById.has(l));
  let temp: LineId[] | null = null;

  o.el.innerHTML = `
    <div class="bar"><span class="count"></span></div>
    <ul class="lines">
      ${o.ds.lines
        .map(
          (l, i) => `${l.group !== o.ds.lines[i - 1]?.group ? `<li class="group" data-group="${l.group}"><span>${GROUP_TITLE[l.group]}</span><button type="button" class="link"></button></li>` : ""}<li data-line="${l.id}">
            <button type="button" class="open">${badge(l.id, l.color)}<span>${escapeHtml(l.name)}</span></button>
            <button type="button" class="sw" role="switch" aria-label="Ver la ${l.id} en el mapa"><i></i></button>
          </li>`,
        )
        .join("")}
    </ul>`;

  const groupLines = (g: string): LineId[] => o.ds.lines.filter((l) => l.group === g).map((l) => l.id);

  const apply = (): void => {
    const shown = new Set(temp ?? on);
    for (const l of o.ds.lines) o.adapter.setLineVisible(l.id, shown.has(l.id));
    o.adapter.setStopsLineFilter(shown);
    o.el.querySelectorAll<HTMLLIElement>("li[data-line]").forEach((li) => {
      const isOn = on.includes(li.dataset["line"]!);
      li.classList.toggle("on", isOn);
      li.querySelector(".sw")?.setAttribute("aria-checked", String(isOn));
    });
    o.el.querySelectorAll<HTMLLIElement>("li[data-group]").forEach((li) => {
      const all = groupLines(li.dataset["group"]!).every((id) => on.includes(id));
      li.querySelector("button")!.textContent = all ? "Ocultar" : "Mostrar";
    });
    o.el.querySelector(".count")!.textContent =
      on.length === 0 ? "Ninguna en el mapa" : `${on.length} de ${o.ds.lines.length} en el mapa`;
    persistSet(STATE_KEY, { on });
    o.onChange?.(temp ?? on);
  };

  o.el.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    // Grupo: si ya se ven todas, las oculta; si no, muestra las que falten.
    const group = t.closest("button") ? t.closest<HTMLLIElement>("li[data-group]")?.dataset["group"] : undefined;
    if (group) {
      const ids = groupLines(group);
      on = ids.every((id) => on.includes(id))
        ? on.filter((id) => !ids.includes(id))
        : [...on, ...ids.filter((id) => !on.includes(id))];
      temp = null;
      apply();
      return;
    }
    const li = t.closest<HTMLLIElement>("li[data-line]");
    if (!li) return;
    const id = li.dataset["line"]!;
    if (t.closest(".sw")) {
      on = on.includes(id) ? on.filter((x) => x !== id) : [...on, id];
      temp = null;
      apply();
    } else {
      o.onOpenLine(id);
    }
  });

  apply();

  return {
    get on() {
      return on;
    },
    setOn(next) {
      on = next.filter((l) => o.ds.linesById.has(l));
      temp = null;
      apply();
    },
    setTemporary(next) {
      temp = next ? next.slice() : null;
      apply();
    },
  };
}
